import { SiteUrls } from '@tableau/shared-urls';
import _ from 'underscore';

import { ActionHelpers } from '~commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '~commonInfra/contentActions/MultipleItemResult';
import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { Set } from '~commonInfra/utils/Set';
import { SetDisplayTabsAction } from '~src/content/services/SetDisplayTabsAction';
import { IDataSourceBase, IDataSourceDetailInfo, IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import {
  ChangeDatasourceRemoteQueryAgentAction
} from '~src/datasource/services/ChangeDatasourceRemoteQueryAgentAction';
import { ChangeDatasourcesTagsAction } from '~src/datasource/services/ChangeDatasourcesTagsAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { CreateProjectAction } from '~src/projects/actions/CreateProjectAction';
import { MoveToProjectAction } from '~src/projects/actions/MoveToProjectAction';
import { IProjectBase } from '~src/projects/models/IProjectInfo';
import { ChangeContentOwnerAction } from '~src/spacesPlatform/actions/ChangeContentOwnerAction';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { SpaceInfoItemTypeValue } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { INamedItemInfo, ISpaceTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { ChangeContentTagsAction } from '~src/tags/services/ChangeContentTagsAction';
import { IViewBase, IViewDetailInfo } from '~src/views/models/IViewInfo';
import { Views } from '~src/views/resources/Views';
import { ChangeViewsTagsAction } from '~src/views/services/ChangeViewsTagsAction';
import { IWorkbookBase, IWorkbookDetailInfo } from '~src/workbook/models/IWorkbookInfo';
import { Workbooks } from '~src/workbook/resources/Workbooks';
import { ChangeWorkbooksTagsAction } from '~src/workbook/services/ChangeWorkbooksTagsAction';


class ContentActions {

  // Move to Project

  public moveWorkbooksToProject(workbooks: Set<IWorkbookBase>): Promise<IResult> {
    return MoveToProjectAction.moveWorkbooks(workbooks);
  }

  public moveDatasourcesToProject(datasources: Set<IDataSourceBase>): Promise<IResult> {
    return MoveToProjectAction.moveDatasources(datasources);
  }

  // Create project
  // Returns the new project id
  public createProject(parentId?: string): Promise<IResult> {
    return CreateProjectAction.showCreateProjectDialog(parentId);
  }

  // Helpers

  private singleton<T extends INamedItemInfo>(item: T) {
    return Set.singleton(item, (item: T) => item.id);
  }

  // Download

  public changeOwnerOnDatasources(datasources: Set<IDataSourceBase>): Promise<IResult> {
    return ChangeContentOwnerAction.confirmChangeOwner(datasources.toArray());
  }

  public changeOwnerOnProjects(projects: Set<IProjectBase>): Promise<IResult> {
    return ChangeContentOwnerAction.confirmChangeOwner(projects.toArray());
  }

  public changeOwnerOnWorkbooks(workbooks: Set<IWorkbookBase>): Promise<IResult> {
    return ChangeContentOwnerAction.confirmChangeOwner(workbooks.toArray());
  }

  // Authoring

  private authoring(authoringUrl: string) {
    WindowLocationService.open(authoringUrl);
  }

  private generateUniqueAuxiliaryId() {
    const base36Digit = () => {
      const s = '0123456789abcdefghijklmnopqrstuvwxyz';
      return s[Math.floor(Math.random() * s.length)];
    };

    const b36 = (len: number) => {
      let s = '';
      for (let i = 0; i < len; ++i) {
        s += base36Digit();
      }
      return s;
    };

    // To generate the auxiliary ID, use a base-32 encoding of the client time-stamp plus a guid-like base-36 pseudo-unique identifier.
    // We use half the number of digits compared to a standard GUID since base-36 has over twice the number of encoding bits.
    // A collision of identifiers generated in this way is virtually impossible.
    return (new Date()).getTime().toString(32).toLowerCase() + '$' + b36(4) + '-' + b36(2) + '-' + b36(2) + '-' + b36(2) + '-' + b36(6);
  }

  public editWorkbook(workbook: IWorkbookDetailInfo) {
    this.authoring(workbook.editUrl);
  }

  public editView(view: IViewDetailInfo) {
    this.authoring(view.editUrl);
  }

  public newFlow(siteUrlName: string, spaceItemInfo: ISpaceTypeInfo, projectId?: string) {
    this.authoring(this.generateNewFlowUrl(siteUrlName, spaceItemInfo, projectId));
  }

  private generateNewPredictiveModelUrl(siteUrlName: string, luid?: string) {
    const predictiveModelEditorSiteUrl = new SiteUrls({ siteUrlName }).predictiveModelEditorUrl();
    const idParam = luid ? `/${luid}` : '';
    return `${predictiveModelEditorSiteUrl}${idParam}`;
  }

  public newPredictiveModel(siteUrlName: string) {
    this.authoring(this.generateNewPredictiveModelUrl(siteUrlName));
  }

  public editPredictiveModel(siteUrlName: string, luid: string) {
    this.authoring(this.generateNewPredictiveModelUrl(siteUrlName, luid));
  }

  public newPublishedConnection(projectLuid?: string) {
    this.authoring(this.publishedConnectionEditorUrlForNewPublishedConnection(projectLuid));
  }

  public editPublishedConnection(publishedConnectionId: string) {
    this.authoring(this.publishedConnectionEditorUrlForExistingPublishedConnection(publishedConnectionId));
  }

  public editPublishedConnectionDraft(draftLuid: string) {
    this.authoring(this.publishedConnectionEditorUrlForDraft(draftLuid));
  }

  private publishedConnectionEditorUrlForNewPublishedConnection(projectLuid?: string) {
    if (projectLuid) {
      return `${this.publishedConnectionEditorPrefix}?project=${projectLuid}`;
    } else {
      return this.publishedConnectionEditorPrefix;
    }
  }

  private publishedConnectionEditorUrlForDraft(draftLuid: string) {
      return `${this.publishedConnectionEditorPrefix}?draft=${draftLuid}`;
  }

  private publishedConnectionEditorUrlForExistingPublishedConnection(publishedConnectionId: string) {
    return `${this.publishedConnectionEditorPrefix}?published-connection=${publishedConnectionId}`;
  }

  private readonly publishedConnectionEditorPrefix = '/published-connection-editor/';

  public editFlow(siteUrlName: string, flowLuid: string) {
    this.authoring(this.generateEditFlowUrl(siteUrlName, flowLuid));
  }

  public editUnpublishedFlowDraft(urls: SpacesSiteUrls, draftLuid: string) {
    this.authoring(urls.flowEditorDraftUrl(draftLuid));
  }

  public generateNewFlowUrl(siteUrlName: string, spaceItemInfo: ISpaceTypeInfo, projectId?: string) {
    const urlSearchParams = new URLSearchParams();
    if (spaceItemInfo.contentType === SpaceInfoItemTypeValue.datasource) {
      urlSearchParams.set('datasource', (spaceItemInfo as IDataSourceDetailInfo).urlName);
    }
    if (projectId) {
      urlSearchParams.set('projectId', projectId);
    }
    const urlSearchTerm: string = urlSearchParams.toString();
    const siteUrl = new SiteUrls({ siteUrlName });
    const flowLuid = 'new';

    return `${siteUrl.flowEditorFlowPath({ flowLuid: 'new' })}${urlSearchTerm ? `?${urlSearchTerm}` : ''}`;
  }

  public generateEditFlowUrl(siteUrlName: string, flowLuid: string) {
    const siteUrl = new SiteUrls({ siteUrlName });
    return siteUrl.flowEditorFlowPath({ flowLuid: flowLuid });
  }

  public newEmptyWorkbook(siteName: string, projectName?: string) {
    this.authoring(this.generateNewWorkbookUrl('newWorkbook', siteName, projectName));
  }

  public generateNewWorkbookFromDatasourceUrl(datasource: IDataSourceDetailInfo, siteName: string) {
    return `${this.generateNewWorkbookUrl('authoringNewWorkbook', siteName)}/${datasource.urlName}`;
  }

  public generateTempWorkbookFromDatasourceUrlForEdit(datasource: IDataSourceDetailInfo, siteName: string) {
    return `${this.generateNewWorkbookUrl('editDatasource', siteName)}/${datasource.urlName}`;
  }

  public generateTempWorkbookForCreateDatasource(siteName: string, projectName?: string) {
    this.authoring(this.generateNewWorkbookUrl('createPublishedDatasource', siteName, projectName));
  }

  public generateNewWorkbookUrl(newWorkbookUrlName: string, siteName: string, projectName?: string) {
    return (siteName === '' ? '' : '/t/' + siteName) +
      '/' + newWorkbookUrlName + '/' +
      (!projectName || projectName === '' ? '' : 'p/' + projectName + '/') +
      this.generateUniqueAuxiliaryId();
  }

  public newMetric(datasource: IDataSourceDetailInfo, siteName: string) {
    WindowLocationService.open(this.generateNewMetricForDatasourceUrl(datasource, siteName));
  }

  public generateNewMetricForDatasourceUrl(datasource: IDataSourceDetailInfo, siteName: string) {
    let site = siteName === '' ? '' : '/t/' + siteName;
    return `${site}/metrics/new/${this.generateUniqueAuxiliaryId()}?:ds=${datasource.urlName}&:dsId=${datasource.id}`;
  }

  // Change Tags
  // Change tags section to be removed with removal of reactDetailsDialogs FF
  // replaced with changeTagsForContent in ChangeTagsActionHelper
  public setTagsOnViews(views: Set<IViewBase>): Promise<IResult> {
    return ChangeViewsTagsAction.execute(views);
  }

  public setTagsOnView(view: IViewBase, newTagsList: string[]): Promise<IResult> {
    const tagsToAdd = _.difference(newTagsList, view.tags);
    const tagsToRemove = _.difference(view.tags, newTagsList);

    return Views.changeTags([view.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray([view]),
        result,
        ChangeViewsTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IViewBase>) => ChangeViewsTagsAction.changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnWorkbook(workbook: IWorkbookBase, newTagsList: string[]): Promise<IResult> {
    const tagsToAdd = _.difference(newTagsList, workbook.tags);
    const tagsToRemove = _.difference(workbook.tags, newTagsList);

    return Workbooks.changeTags([workbook.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray([workbook]),
        result,
        ChangeWorkbooksTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IWorkbookBase>) => ChangeWorkbooksTagsAction.changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnWorkbooks(workbooks: Set<IWorkbookBase>): Promise<IResult> {
    return ChangeWorkbooksTagsAction.execute(workbooks);
  }

  public setTagsOnDatasource(datasource: IDataSourceInfo, newTagsList: string[]): Promise<IResult> {
    const tagsToAdd = _.difference(newTagsList, datasource.tags);
    const tagsToRemove = _.difference(datasource.tags, newTagsList);

    return DataSources.changeTags([datasource.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray([datasource]),
        result,
        ChangeDatasourcesTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IDataSourceBase>) => ChangeDatasourcesTagsAction.changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnDatasources(datasources: Set<IDataSourceBase>): Promise<IResult> {
    return ChangeDatasourcesTagsAction.showChangeTagsDialog(datasources);
  }

  public setTagsOnMixedContent(content: IContentInfo[]): Promise<IResult> {
    return ChangeContentTagsAction.showChangeTagsForMixedContentDialog(content);
  }

  // Set Display Tabs

  public setDisplayTabsOnWorkbook(workbooks: Set<IWorkbookBase>): Promise<IResult> {
    return SetDisplayTabsAction.confirmSetDisplayTabs(workbooks);
  }

  public changeRemoteQueryAgentOnDatasource(datasource: IDataSourceDetailInfo): Promise<IResult> {
    return ChangeDatasourceRemoteQueryAgentAction.execute(datasource);
  }

  public openWhoHasSeenThisView(viewId: string) {
    const whoHasSeenThisViewLink = '/vizql/showadminview/views/WhoHasSeen?views_id=' + viewId;
    WindowLocationService.open(whoHasSeenThisViewLink);
  }
}

export const ContentActionsService = new ContentActions();
// export only the type of the class, since we don't want modules to be able to instantiate it
export type { ContentActions };
